import { ZUORA_HPP_SCRIPT_URL } from './config';
import type { ZuoraGlobal } from './types';

/**
 * Lazily loads the Zuora HPM client library and resolves the `Z` global it
 * installs on `window`. Mirrors the shape of `lib/stripe/stripe.ts` —
 * one instance per app load, cached across calls.
 *
 * On load failure the cached promise is cleared so a subsequent call retries.
 */
let zuoraPromise: Promise<ZuoraGlobal> | undefined;

export const loadZuora = (): Promise<ZuoraGlobal> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('loadZuora called outside the browser'));
  }
  if (window.Z) return Promise.resolve(window.Z);

  zuoraPromise ??= new Promise<ZuoraGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ZUORA_HPP_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Z) {
        resolve(window.Z);
      } else {
        zuoraPromise = undefined;
        reject(new Error('Zuora HPM script loaded but window.Z is undefined'));
      }
    };
    script.onerror = () => {
      zuoraPromise = undefined;
      reject(new Error(`Failed to load Zuora HPM script: ${script.src}`));
    };
    document.head.appendChild(script);
  });

  return zuoraPromise;
};

/** Test-only: reset the cached promise and any installed global. */
export const __resetZuoraForTests = () => {
  zuoraPromise = undefined;
  if (typeof window !== 'undefined') {
    delete window.Z;
  }
};
